import { z } from "zod";
import { responseSchema } from "../http.js";
import { countrySchema, emailSchema } from "./auth.js";
import { currencySchema, priceCentsSchema, volumePointsSchema } from "./product.js";
import { commissionSchema } from "./commission.js";
import { checkoutShippingSelectionSchema, orderShipmentSchema, orderShippingChargeSchema, shippingAddressSchema, } from "./shipping.js";
/**
 * ═══════════════════════════════════════════════════════════
 * Órdenes y compras
 *
 * ── Qué son, y qué son la una para la otra ─────────────────
 * Una ORDEN es el pedido de la tienda: quién compró, qué renglones lleva
 * y cuánto suma. Una COMPRA es el registro que alimenta al plan de
 * compensación: un producto, su importe pagado y los puntos (PV/CV) que
 * genera. En producción son las pantallas `view-orders` y
 * `view-purchases`, y aquí se replican en ese orden.
 *
 * ── Por qué existen ANTES que el checkout ──────────────────
 * Anahí pidió las dos secciones en el panel desde ya (26-ago). El día
 * que exista el checkout de productos será ÉL quien cree órdenes y
 * compras; mientras tanto se crean desde el panel/seed por el MISMO
 * contrato, así que nada de lo guardado quedará fuera de forma cuando
 * llegue el flujo real.
 *
 * ── El cliente es una FOTO, no una referencia ──────────────
 * `customer` guarda nombre y correo tal como eran al comprar. Un pedido
 * es un documento histórico: si mañana la clienta se cambia el nombre,
 * la factura de enero no puede cambiar con ella. El día que el checkout
 * real cree órdenes podrá añadir además su `userId`; la foto se queda.
 *
 * ── El total lo calcula el SERVIDOR ────────────────────────
 * `totalCents` y `amountCents` NO viajan en el alta: salen de sumar
 * renglones (cantidad × precio unitario). Aceptarlos escritos sería una
 * segunda fuente para la misma suma, y las dos se contradicen en cuanto
 * alguien toca una. Es la misma regla que los precios derivados del
 * producto.
 * ═══════════════════════════════════════════════════════════
 */
// ═══════════════════════════════════════════════════════════
// Estados
// ═══════════════════════════════════════════════════════════
/**
 * El ciclo de vida de una orden, en el orden en que ocurre —pendiente,
 * pagada, enviada, entregada, completada— con `cancelled` como salida
 * lateral desde cualquiera. Códigos en inglés como el resto del
 * sistema; el español es del frontend.
 */
export const ORDER_STATUSES = [
    "pending",
    "paid",
    "shipped",
    "delivered",
    "completed",
    "cancelled",
];
export const orderStatusSchema = z.enum(ORDER_STATUSES);
/**
 * Una compra solo tiene dos estados: se pagó, o se devolvió. El detalle
 * fino del envío es de la ORDEN; la compra existe para los puntos, y a
 * los puntos solo les importa si el dinero se quedó.
 */
export const PURCHASE_STATUSES = ["completed", "refunded"];
export const purchaseStatusSchema = z.enum(PURCHASE_STATUSES);
// ═══════════════════════════════════════════════════════════
// El cliente, como foto
// ═══════════════════════════════════════════════════════════
export const orderCustomerSchema = z.object({
    name: z
        .string()
        .trim()
        .min(1, "Escribe el nombre del cliente")
        .max(120, "Ese nombre es demasiado largo"),
    email: emailSchema,
    /**
     * Teléfono y dirección de ENTREGA, como los escribió el cliente en el
     * checkout. Opcionales en el contrato porque las órdenes del
     * panel/seed no los llevan; el checkout de la tienda los exige — son
     * los campos con asterisco de la pantalla de producción.
     */
    phone: z.string().trim().max(30).optional(),
    address: z.string().trim().max(500).optional(),
    /**
     * La MISMA dirección, estructurada como la pide una guía de envío
     * (calle, ciudad, estado, CP). La escribe el checkout desde el 27-ago;
     * `address` queda como el renglón legible que componen esos campos.
     * Opcional porque las órdenes del panel/seed y las anteriores a esa
     * fecha no la llevan — para ellas, la guía se captura al cotizar.
     */
    shippingAddress: shippingAddressSchema.optional(),
});
// ═══════════════════════════════════════════════════════════
// El método de pago
// ═══════════════════════════════════════════════════════════
/**
 * Los tres caminos del dinero (Anahí, 26-ago): tarjeta (Stripe),
 * transferencia y efectivo. Los TRES terminan en validación manual por
 * ahora — pagar con tarjeta abre Stripe pero no marca nada; el panel
 * confirma con el comprobante hasta que exista el webhook.
 */
export const PAYMENT_METHODS = ["card", "transfer", "cash"];
export const paymentMethodSchema = z.enum(PAYMENT_METHODS);
// ═══════════════════════════════════════════════════════════
// Órdenes
// ═══════════════════════════════════════════════════════════
/**
 * Un renglón del pedido. `name` es también una foto: el nombre del
 * producto tal como se vendió, para que archivarlo o renombrarlo después
 * no reescriba pedidos históricos. `productId` queda para poder saltar a
 * la ficha mientras exista.
 */
export const orderItemSchema = z.object({
    productId: z.string().trim().min(1, "Falta el producto"),
    name: z.string().trim().min(1, "Falta el nombre del producto").max(200),
    quantity: z
        .number()
        .int("Las unidades son enteras")
        .min(1, "Al menos una unidad")
        .max(99, "De 1 a 99 unidades"),
    unitPriceCents: priceCentsSchema,
});
/** Lo que entra al crear una orden. Sin total: lo suma el servidor. */
export const orderInputSchema = z.object({
    customer: orderCustomerSchema,
    currency: currencySchema,
    items: z
        .array(orderItemSchema)
        .min(1, "Una orden sin renglones no es una orden")
        .max(50, "Demasiados renglones para un solo pedido"),
    status: orderStatusSchema.default("pending"),
});
// ═══════════════════════════════════════════════════════════
// El comprobante de pago
// ═══════════════════════════════════════════════════════════
/**
 * La validación MANUAL de un pago (Anahí, 26-ago): quien paga por
 * transferencia manda su comprobante, y en el panel se sube a la orden
 * antes de marcarla como pagada. Formatos: lo que produce un teléfono
 * (foto) o un banco (PDF). Una lista cerrada, no "cualquier archivo":
 * un .exe subido como comprobante no es un accidente.
 */
export const PROOF_MIME_TYPES = [
    "image/jpeg",
    "image/png",
    "image/webp",
    "application/pdf",
];
export const proofMimeSchema = z.enum(PROOF_MIME_TYPES);
/** El techo del archivo, en bytes del ARCHIVO (no del base64). */
export const PROOF_MAX_BYTES = 4 * 1024 * 1024;
/**
 * El comprobante viaja como base64 dentro del JSON, sin prefijo
 * `data:`. No hay infraestructura de subida de archivos todavía —las
 * imágenes de producto van por URL por lo mismo— pero un comprobante
 * es un archivo que vive en el teléfono de quien pagó, no en un
 * servidor: aquí la subida de verdad no es opcional. Base64 en JSON es
 * la forma más simple que pasa por el mismo borde validado que todo lo
 * demás; el día que exista un almacén de archivos, cambia el
 * transporte y este contrato, y nada más.
 *
 * El techo del base64 es el del archivo más el ~33 % que el base64
 * infla, con un margen para el padding.
 */
export const orderProofInputSchema = z.object({
    fileName: z.string().trim().min(1, "Falta el nombre del archivo").max(200),
    mimeType: proofMimeSchema,
    dataBase64: z
        .string()
        .min(1, "El archivo llegó vacío")
        .max(Math.ceil(PROOF_MAX_BYTES / 3) * 4 + 8, "El comprobante no puede pasar de 4 MB"),
});
/**
 * Cómo sale el comprobante en la orden. `dataUrl` (un `data:` listo
 * para `<img src>` o para descargar) SOLO viaja en el DETALLE de una
 * orden: en el listado sería megabytes por fila que nadie mira.
 */
export const orderPaymentProofSchema = z.object({
    fileName: z.string(),
    mimeType: proofMimeSchema,
    sizeBytes: z.number().int().min(0),
    uploadedAt: z.string(),
    dataUrl: z.string().optional(),
});
// ═══════════════════════════════════════════════════════════
// El cambio manual de estado
// ═══════════════════════════════════════════════════════════
/** Lo único que se puede tocar de una orden ya creada: su estado. */
export const orderStatusUpdateSchema = z.object({
    status: orderStatusSchema,
});
/**
 * Los estados que significan "el dinero ya entró". Llegar a cualquiera
 * de ellos por primera vez CONVIERTE la orden en compras (Anahí,
 * 27-ago: los ÚNICOS que no la convierten son pendiente y cancelada).
 * Ver la nota de `purchasedAt` en `orderSchema`.
 */
export const PAID_ORDER_STATUSES = [
    "paid",
    "shipped",
    "delivered",
    "completed",
];
// ═══════════════════════════════════════════════════════════
// El checkout del cliente
// ═══════════════════════════════════════════════════════════
/**
 * Lo que manda el carrito: QUÉ y CUÁNTOS. Nunca el precio ni el nombre
 * — los dos los resuelve el servidor del catálogo, por la misma regla
 * que el cobro de la franquicia: si el navegador pudiera mandar el
 * importe, la primera persona que abra las herramientas del navegador
 * compraría al precio que quisiera.
 */
export const orderCheckoutItemSchema = z.object({
    productId: z.string().trim().min(1, "Falta el producto"),
    quantity: z
        .number()
        .int("Las unidades son enteras")
        .min(1, "Al menos una unidad")
        .max(99, "De 1 a 99 unidades"),
});
/**
 * Los campos que la PANTALLA de compra pide, en plano.
 *
 * ── Por qué existe, si ya está `orderCheckoutRequestSchema` ──────────
 * Anahí (1-sep): «debes agregar validaciones en todos los campos». El
 * modal de compra de la tienda no validaba ni uno: todo viajaba al
 * servidor y volvía UN aviso para seis campos, sin decir cuál.
 *
 * El esquema de la petición tiene la dirección ANIDADA
 * (`shippingAddress.street`), y una pantalla necesita las llaves planas
 * para colgar el error debajo de cada caja. Componerlo aquí a partir de
 * `.shape` —y no volviendo a escribir las reglas— mantiene UNA sola
 * definición de "una calle válida": si mañana el mínimo de la calle
 * cambia allá, cambia aquí solo.
 *
 * Es a propósito lo que la pantalla PIDE, no todo lo que se manda: la
 * moneda, los renglones y la tienda los pone el código, no quien compra.
 */
export const checkoutFormSchema = z.object({
    /** Opcional de verdad: sin él, el servidor usa el nombre de la cuenta. */
    fullName: z.string().trim().max(120, "Ese nombre es demasiado largo").optional(),
    phone: z
        .string()
        .trim()
        .min(7, "Escribe un teléfono para contactarte sobre el envío")
        .max(30, "Ese teléfono es demasiado largo"),
    street: shippingAddressSchema.shape.street,
    city: shippingAddressSchema.shape.city,
    state: shippingAddressSchema.shape.state,
    zip: shippingAddressSchema.shape.zip,
});
/**
 * Los techos de cada caja, para el `maxLength` de la pantalla. Salen de
 * aquí y no de números escritos a mano en el JSX: un tope que no
 * coincide con su esquema es un campo que deja teclear lo que después se
 * rechaza, que es exactamente el problema que esto vino a arreglar.
 */
export const CHECKOUT_FIELD_MAX = {
    fullName: 120,
    phone: 30,
    street: 200,
    city: 80,
    state: 60,
    zip: 5,
};
export const orderCheckoutRequestSchema = z.object({
    currency: currencySchema,
    items: z
        .array(orderCheckoutItemSchema)
        .min(1, "El carrito está vacío")
        .max(50, "Demasiados renglones para un solo pedido"),
    /**
     * Los datos de la pantalla de checkout (réplica de producción):
     * teléfono y dirección son obligatorios AQUÍ aunque el contrato de la
     * orden los tenga opcionales — el panel puede capturar un pedido sin
     * ellos; un cliente que espera su paquete, no.
     */
    /**
     * Quién RECIBE, "tal como aparece en tu identificación" (producción).
     * Opcional: sin él, el servidor usa el nombre de la cuenta. Existe
     * porque quien compra no siempre es quien recibe el paquete.
     */
    fullName: z.string().trim().min(1).max(120).optional(),
    phone: z
        .string()
        .trim()
        .min(7, "Escribe un teléfono para contactarte sobre el envío")
        .max(30),
    /**
     * ESTRUCTURADA desde el 27-ago (integración Trasso): una guía de envío
     * exige calle, ciudad, estado y CP de 5 dígitos por separado, y el
     * único momento en que el cliente los escribe es aquí. El renglón
     * legible (`customer.address`) lo compone el servidor con estos campos.
     */
    shippingAddress: shippingAddressSchema,
    paymentMethod: paymentMethodSchema,
    /**
     * Presente cuando se compra EN una tienda personalizada: el servidor
     * valida el stock del franquiciatario, cobra al precio de la tienda
     * (el de socio) y le atribuye la venta.
     */
    storeSlug: z.string().trim().min(1).optional(),
    /**
     * El cupón que el cliente tecleó al final del checkout (Anahí,
     * 27-ago). Solo viaja el CÓDIGO: el descuento lo calcula el servidor
     * contra el cupón real — vigencia, usos y moneda incluidos.
     */
    couponCode: z.string().trim().toUpperCase().optional(),
    /**
     * El envío que eligió (13-oct-2026): QUÉ cotización y QUÉ servicio.
     * Obligatorio cuando la cotización del checkout dijo `required`; el
     * monto lo pone el servidor desde la foto que guardó al cotizar.
     */
    shippingSelection: checkoutShippingSelectionSchema.optional(),
});
/**
 * El pago del pedido, en tres finales:
 *
 *   · `ready`          → tarjeta, y hay link de Stripe: redirige.
 *   · `not_configured` → tarjeta, pero sin llaves todavía: se dice.
 *   · `manual`         → transferencia o efectivo: no hay nada que
 *                        abrir; las instrucciones las pinta la pantalla
 *                        y el pago se valida en el panel.
 *
 * La VALIDACIÓN es manual en LOS TRES (decisión de Anahí, 26-ago):
 * pagar en Stripe no marca la orden como pagada — eso lo hace el panel,
 * comprobante en mano, hasta que exista el webhook.
 */
export const orderPaymentSchema = z.discriminatedUnion("status", [
    z.object({ status: z.literal("ready"), url: z.string().min(1) }),
    z.object({ status: z.literal("not_configured") }),
    z.object({ status: z.literal("manual") }),
]);
// ═══════════════════════════════════════════════════════════
// Adquirir la franquicia
// ═══════════════════════════════════════════════════════════
/**
 * El pedido de FRANQUICIA (Anahí, 26-ago): la franquicia digital más el
 * PRIMER STOCK — un paquete mayorista elegido del modal. El servidor
 * arma los dos renglones y suma: el costo de la franquicia sale de
 * `FRANCHISE_PRICE_CENTS` y el del paquete, del catálogo. Del navegador
 * solo viaja QUÉ paquete y en qué moneda.
 *
 * El pago nace como link de Stripe (tarjeta); la validación en el panel
 * acepta también transferencia o efectivo, como todo lo demás.
 */
export const franchiseOrderRequestSchema = z.object({
    currency: currencySchema,
    /** El paquete mayorista (categoría `franchisee`) del primer stock. */
    packageId: z.string().trim().min(1, "Elige tu primer stock"),
    phone: z
        .string()
        .trim()
        .min(7, "Escribe un teléfono para contactarte")
        .max(30),
    /**
     * ── Con franquicia, o SOLO el paquete (Anahí, 29-ago) ──────────────
     * La pantalla de adquirir tiene dos salidas y las dos terminan aquí:
     * "Confirmar compra" trae la franquicia más su primer stock;
     * "Comprar directo" trae únicamente el paquete, para quien no la
     * quiere. Un solo camino para las dos, porque lo que cambia es UN
     * renglón del pedido y no el flujo: misma dirección, mismo método de
     * pago, misma validación.
     *
     * Por omisión `true`: quien ya llamaba a este endpoint compraba la
     * franquicia, y un valor por defecto distinto cambiaría en silencio lo
     * que compra.
     */
    withFranchise: z.boolean().default(true),
    /**
     * A dónde se envía. Obligatoria: un paquete son cajas que alguien
     * tiene que entregar, y sin dirección la guía de Trasso no existe.
     */
    shippingAddress: shippingAddressSchema,
    /**
     * Cómo paga. Con `card` se abre Stripe; con transferencia o efectivo
     * el pedido nace pendiente y se valida en el panel, igual que
     * cualquier otro.
     */
    paymentMethod: paymentMethodSchema,
    /** El envío elegido en el checkout (13-oct-2026), igual que en el carrito. */
    shippingSelection: checkoutShippingSelectionSchema.optional(),
});
/**
 * TODO folio empieza con "ITB" (Anahí, 26-ago): es el número que se lee
 * por teléfono y lleva la marca por delante. La serie del medio dice de
 * qué es — "ITB-ORD-000001" para órdenes, "ITB-CMP-000001" para
 * compras. Lo asigna el SERVIDOR; el contrato solo lo hace cumplir, para
 * que un folio sin marca no pueda ni salir del API.
 */
export const folioSchema = z
    .string()
    .startsWith("ITB-", 'Los folios empiezan con "ITB"');
/**
 * La orden completa, como sale del API. `folio` lo asigna el servidor:
 * es el número que se lee por teléfono, no el id de la base. `createdAt`
 * viaja como instante ISO, igual que en el resto de los contratos.
 *
 * ── `purchasedAt` y `purchaseFolios`: la conversión ─────────────────
 * "Cambiando el estado, una orden se vuelve una compra" (Anahí,
 * 26-ago). La PRIMERA vez que una orden llega a un estado de
 * `PAID_ORDER_STATUSES`, el servidor crea una compra por cada renglón
 * —con los PV/CV del producto en ese momento— y deja aquí la fecha y
 * los folios creados. La conversión ocurre UNA vez: mover el estado
 * después (pagada → enviada → entregada) no duplica compras, y esos
 * dos campos son la prueba y el enlace.
 */
export const orderSchema = orderInputSchema.extend({
    id: z.string(),
    folio: folioSchema,
    totalCents: priceCentsSchema,
    /** Cómo dijo que pagaría. Ausente en órdenes del panel/seed. */
    paymentMethod: paymentMethodSchema.optional(),
    /**
     * La TIENDA PERSONALIZADA donde se compró, si fue en una. Con ella
     * viaja `sellerUserId`: el franquiciatario dueño de esa tienda — es
     * el hilo de "sus ventas" en su panel.
     */
    storeSlug: z.string().optional(),
    sellerUserId: z.string().optional(),
    /**
     * `true` cuando el pedido ES la adquisición de una franquicia (el
     * cargo de la franquicia + su primer stock mayorista). Es lo que la
     * conversión mira para volver FRANQUICIATARIO a quien la compró al
     * validarse el pago (Anahí, 26-ago).
     */
    isFranchise: z.boolean().optional(),
    /**
     * Quién la creó desde la tienda, si fue un cliente con sesión. Las
     * órdenes del panel/seed no lo llevan. Es el hilo del que cuelga
     * "se vuelve socio": al validarse su primera orden, esta referencia
     * dice a quién.
     */
    userId: z.string().optional(),
    paymentProof: orderPaymentProofSchema.optional(),
    purchasedAt: z.string().optional(),
    purchaseFolios: z.array(folioSchema).optional(),
    /**
     * El cupón aplicado y cuánto restó, como FOTO: si el cupón se apaga
     * mañana, este pedido ya lo usó. `totalCents` viaja YA descontado —
     * es lo que se cobra; el desglose existe para poder enseñarlo.
     */
    couponCode: z.string().optional(),
    discountCents: priceCentsSchema.optional(),
    /**
     * La GUÍA de Trasso, cuando ya se generó: paquetería, rastreo, estado
     * y el precio que se descontó del wallet. El PDF no vive aquí — su URL
     * firmada caduca en 1 hora y se pide fresca (`SHIPPING_ROUTES`).
     */
    shipping: orderShipmentSchema.optional(),
    /**
     * Lo que el comprador PAGÓ de envío (13-oct-2026), ya sumado en
     * `totalCents`: total = renglones − cupón + envío. No es venta — las
     * compras, los puntos y las comisiones salen de los renglones.
     */
    shippingCents: priceCentsSchema.optional(),
    shippingCharge: orderShippingChargeSchema.optional(),
    /**
     * El último link de pago de Stripe de esta orden (Anahí, 22-oct-2026):
     * para reabrirlo si el cliente lo pierde o si el vendedor cobra por él.
     * Stripe lo vence a las 24 h; `expiresAt` dice hasta cuándo sirve.
     */
    stripeCheckout: z
        .object({ url: z.string(), expiresAt: z.string().nullable(), createdAt: z.string() })
        .optional(),
    createdAt: z.string(),
});
// ═══════════════════════════════════════════════════════════
// Compras
// ═══════════════════════════════════════════════════════════
/**
 * Lo que entra al registrar una compra.
 *
 * `volumePoints` (PV) y `commissionPoints` (CV) se escriben aquí como
 * FOTO del producto en el momento de comprar — el mismo trato que el
 * precio. Si el producto cambia sus puntos mañana, las compras de hoy
 * conservan los que generaron: el corte mensual no puede recalcularse
 * bajo los pies de nadie.
 */
export const purchaseInputSchema = z.object({
    customer: orderCustomerSchema,
    productName: z.string().trim().min(1, "Falta el producto").max(200),
    quantity: z
        .number()
        .int("Las unidades son enteras")
        .min(1, "Al menos una unidad")
        .max(99, "De 1 a 99 unidades"),
    unitPriceCents: priceCentsSchema,
    currency: currencySchema,
    volumePoints: volumePointsSchema.default(0),
    commissionPoints: volumePointsSchema.default(0),
    status: purchaseStatusSchema.default("completed"),
    /**
     * El PAÍS del comprador (el que declaró al registrarse): es la
     * dimensión por la que contabilidad corta las ventas. La conversión
     * lo copia de la cuenta; en compras del panel es opcional.
     */
    country: countrySchema.optional(),
});
export const purchaseSchema = purchaseInputSchema.extend({
    id: z.string(),
    folio: folioSchema,
    /**
     * Lo que se PAGÓ por el renglón, sumado por el servidor: cantidad ×
     * precio unitario, menos su parte del descuento si la orden llevó
     * cupón. Es el número que suma el dashboard financiero.
     */
    amountCents: priceCentsSchema,
    createdAt: z.string(),
});
// ═══════════════════════════════════════════════════════════
// Listados
// ═══════════════════════════════════════════════════════════
export const orderListQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    perPage: z.coerce.number().int().min(1).max(100).default(24),
    /**
     * Los filtros van AL SERVIDOR (Anahí, 27-ago): la lista pide solo lo
     * que se ve, nunca todo para filtrar en el navegador. Búsqueda por
     * folio, nombre o correo del cliente; estado exacto.
     */
    query: z.string().trim().max(120).optional(),
    status: orderStatusSchema.optional(),
    /** YYYY-MM-DD, inclusivo — el rango de fechas de la lista. */
    from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    /** El total del pedido, en PESOS/DÓLARES enteros (la gente filtra
     * "desde 1000", no "desde 100000 centavos"). */
    minTotal: z.coerce.number().min(0).optional(),
    maxTotal: z.coerce.number().min(0).optional(),
});
/**
 * `/api/orders/mine`: los pedidos de la sesión. `buyer` = lo que YO
 * compré; `seller` = lo que vendió MI tienda personalizada.
 */
export const myOrdersQuerySchema = orderListQuerySchema.extend({
    role: z.enum(["buyer", "seller"]).default("buyer"),
});
export const orderListSchema = z.object({
    items: z.array(orderSchema),
    total: z.number().int().min(0),
    page: z.number().int().min(1),
    perPage: z.number().int().min(1),
});
/**
 * El listado/dashboard de compras filtra por RANGO de fechas y país —
 * lo que el equipo financiero corta cada mes (Anahí, 27-ago).
 */
export const purchasesAdminQuerySchema = orderListQuerySchema.extend({
    /** YYYY-MM-DD, inclusivo. */
    from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    country: countrySchema.optional(),
});
/** Un monto por moneda: las ventas NO se suman entre monedas. */
export const revenueByCurrencySchema = z.array(z.object({ currency: currencySchema, amountCents: priceCentsSchema }));
/**
 * El resumen financiero del periodo: los números que contabilidad
 * necesita para saber el estado de las compras del mes.
 */
export const purchaseSummarySchema = z.object({
    totals: z.object({
        purchases: z.number().int().min(0),
        units: z.number().int().min(0),
        volumePoints: z.number().int().min(0),
        commissionPoints: z.number().int().min(0),
        /** Compradores DISTINTOS (por correo) en el periodo. */
        clients: z.number().int().min(0),
        revenue: revenueByCurrencySchema,
    }),
    byCountry: z.array(z.object({
        /** "MX", "US", "OTHER" — o "—" para compras sin país registrado. */
        country: z.string(),
        purchases: z.number().int().min(0),
        units: z.number().int().min(0),
        revenue: revenueByCurrencySchema,
    })),
    byDay: z.array(z.object({
        /** YYYY-MM-DD. */
        date: z.string(),
        purchases: z.number().int().min(0),
        revenue: revenueByCurrencySchema,
    })),
});
export const purchaseSummaryResponseSchema = responseSchema(purchaseSummarySchema);
export const purchaseListSchema = z.object({
    items: z.array(purchaseSchema),
    total: z.number().int().min(0),
    page: z.number().int().min(1),
    perPage: z.number().int().min(1),
});
// ═══════════════════════════════════════════════════════════
// Respuestas y rutas
// ═══════════════════════════════════════════════════════════
export const orderListResponseSchema = responseSchema(orderListSchema);
export const orderResponseSchema = responseSchema(z.object({ order: orderSchema }));
/**
 * El DETALLE del panel trae también las compras que la orden generó al
 * convertirse (vacío mientras siga pendiente): son los puntos y el
 * dinero que el panel enseña junto al pedido, sin una segunda petición.
 */
export const orderDetailResponseSchema = responseSchema(z.object({
    order: orderSchema,
    purchases: z.array(purchaseSchema),
    /**
     * Las comisiones REALMENTE registradas por esta orden (el motor
     * corre al validar). Vacío = no generó — y el panel no pinta nada:
     * enseñar un reparto que no ocurrió confunde (Anahí, 27-ago).
     */
    commissions: z.array(commissionSchema.extend({ userName: z.string() })),
}));
export const purchaseListResponseSchema = responseSchema(purchaseListSchema);
export const purchaseResponseSchema = responseSchema(z.object({ purchase: purchaseSchema }));
/**
 * La respuesta del cambio de estado trae también las compras que ese
 * cambio CREÓ (vacío si no convirtió nada): el panel las anuncia con
 * sus folios en vez de que aparezcan en la otra pantalla sin aviso.
 */
export const orderStatusResponseSchema = responseSchema(z.object({
    order: orderSchema,
    createdPurchases: z.array(purchaseSchema),
}));
/** La respuesta del checkout: el pedido creado y cómo pagarlo. */
export const orderCheckoutResponseSchema = responseSchema(z.object({
    order: orderSchema,
    payment: orderPaymentSchema,
    /**
     * SOLO en pedidos de TIENDA PERSONALIZADA (Anahí, 27-ago): la
     * cuenta del FRANQUICIATARIO a la que el cliente transfiere. En
     * esas tiendas no hay Stripe — se paga por transferencia o en
     * efectivo, directo con quien vende. `null` si no la ha registrado
     * (la confirmación manda al WhatsApp de la tienda).
     */
    sellerAccount: z
        .union([
        /** EE. UU. (pedido en USD de la casa): ACH, 29-sep-2026. */
        z.object({
            type: z.literal("us_ach"),
            bankName: z.string(),
            holderName: z.string(),
            accountNumber: z.string(),
            routingNumber: z.string(),
            address: z.string().optional(),
            note: z.string().optional(),
        }),
        z.object({
            type: z.literal("clabe").optional(),
            bankName: z.string(),
            clabe: z.string(),
            holderName: z.string(),
            /** Instrucciones extra de la cuenta de la casa: referencia,
             * horarios, a quién avisar. La del franquiciatario no la trae. */
            note: z.string().optional(),
        }),
    ])
        .nullable()
        .optional(),
}));
/** Registrar el PAGO de una venta de la tienda (el franquiciatario):
 * comprobante opcional — la validación descuenta el stock y dispara la
 * contabilidad, exactamente como la validación del panel. */
export const paySaleRequestSchema = z.object({
    proof: orderProofInputSchema.optional(),
});
export const paySaleResponseSchema = responseSchema(z.object({ order: orderSchema }));
/**
 * MIS PUNTOS (Anahí, 27-ago: el socio "debe poder visualizar sus
 * puntos"): la suma de PV y CV de sus compras VALIDADAS — las fotos
 * que el motor congeló al validar, no un recálculo.
 */
export const myPointsResponseSchema = responseSchema(z.object({
    volumePoints: z.number().int().min(0),
    commissionPoints: z.number().int().min(0),
    purchases: z.number().int().min(0),
}));
/**
 * Sin gemelo público: los pedidos y las compras son del panel. El día
 * que exista "mis pedidos" para cada cliente, será OTRA ruta con OTRO
 * filtro obligatorio (su sesión), no un parámetro sobre esta.
 */
export const ORDER_ROUTES = {
    adminList: "/api/admin/orders",
    adminCreate: "/api/admin/orders",
    adminOne: (id) => `/api/admin/orders/${id}`,
    adminStatus: (id) => `/api/admin/orders/${id}/status`,
    adminProof: (id) => `/api/admin/orders/${id}/proof`,
    /** Genera un link de pago de Stripe NUEVO para una orden pendiente. */
    adminStripeLink: (id) => `/api/admin/orders/${id}/stripe-link`,
    /**
     * El checkout del CLIENTE: exige sesión (de cualquier rol — comprar
     * es de todo el mundo) pero no panel. Vive fuera de `/admin` porque
     * no es administración: es la tienda cerrando un pedido.
     */
    checkout: "/api/orders/checkout",
    /** Adquirir la franquicia: franquicia + primer stock, en un pedido. */
    franchiseCheckout: "/api/orders/franchise-checkout",
    /** Mis pedidos (como comprador) o mis ventas (como tienda). */
    mine: "/api/orders/mine",
    /** El franquiciatario registra el pago de UNA venta de su tienda. */
    paySale: (id) => `/api/orders/sales/${id}/pay`,
    /** Mis puntos: PV y CV de mis compras validadas. */
    minePoints: "/api/orders/mine/points",
};
export const PURCHASE_ROUTES = {
    adminList: "/api/admin/purchases",
    adminCreate: "/api/admin/purchases",
    /** El resumen financiero del periodo (mismos filtros que el listado). */
    adminSummary: "/api/admin/purchases/summary",
    /** El CSV para Excel del periodo (mismos filtros). */
    adminExport: "/api/admin/purchases/export",
};
//# sourceMappingURL=order.js.map